import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Search, 
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Tag,
  Calendar,
  DollarSign
} from 'lucide-react';
import { personalizedListSubmissionService } from '../lib/database';
import type { PersonalizedListSubmission } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const PersonalizedListAdmin = () => {
  const { user, isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState<PersonalizedListSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState<PersonalizedListSubmission | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Payments' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' }
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchSubmissions();
    }
  }, [isAdmin, searchQuery, statusFilter]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      
      if (statusFilter !== 'all') {
        filters.payment_status = statusFilter;
      }
      
      if (searchQuery) {
        filters.search = searchQuery;
      }
      
      const data = await personalizedListSubmissionService.getAll(filters);
      setSubmissions(data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge bg-success d-flex align-items-center gap-1"><CheckCircle size={12} /> Completed</span>;
      case 'failed':
        return <span className="badge bg-danger d-flex align-items-center gap-1"><XCircle size={12} /> Failed</span>;
      case 'pending':
        return <span className="badge bg-warning d-flex align-items-center gap-1"><Clock size={12} /> Pending</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name', 'Email', 'Exam', 'Rank', 'Category', 'Stream', 'States', 
      'Amount Paid', 'Promo Code', 'Discount', 'Payment Status', 'Payment ID', 'Created At'
    ];
    
    const csvData = submissions.map(submission => [
      submission.name,
      submission.email,
      submission.exam,
      submission.rank,
      submission.category,
      submission.stream,
      submission.states.join('; '),
      submission.amount_paid,
      submission.promo_code || '',
      submission.discount_amount,
      submission.payment_status,
      submission.payment_id || '',
      new Date(submission.created_at).toLocaleDateString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `personalized_list_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Data exported successfully!');
  };

  const viewDetails = (submission: PersonalizedListSubmission) => {
    setSelectedSubmission(submission);
    setShowDetails(true);
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the Personalized List Admin panel.
                </p>
                <Link to="/" className="btn btn-primary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading submissions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-success rounded-3 p-3">
              <Users className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">NEET Predictor Submissions</h1>
              <p className="text-muted mb-0">Manage NEET student submissions and payments</p>
            </div>
          </div>
          <Link to="/manage-colleges" className="btn btn-outline-secondary">
            College Manager
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card bg-primary bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h4 className="fw-bold text-primary mb-1">{submissions.length}</h4>
                <small className="text-muted">Total Submissions</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h4 className="fw-bold text-success mb-1">
                  {submissions.filter(s => s.payment_status === 'completed').length}
                </h4>
                <small className="text-muted">Completed Payments</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h4 className="fw-bold text-warning mb-1">
                  {submissions.filter(s => s.payment_status === 'pending').length}
                </h4>
                <small className="text-muted">Pending Payments</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h4 className="fw-bold text-info mb-1">
                  ₹{submissions.filter(s => s.payment_status === 'completed').reduce((sum, s) => sum + s.amount_paid, 0)}
                </h4>
                <small className="text-muted">NEET Revenue</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="position-relative">
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control ps-5"
                  />
                </div>
              </div>
              <div className="col-md-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-select"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <button
                  onClick={exportToCSV}
                  className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="card border-0 shadow-lg">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 p-3">Student</th>
                    <th className="border-0 p-3">Exam Details</th>
                    <th className="border-0 p-3">Preferences</th>
                    <th className="border-0 p-3">Payment</th>
                    <th className="border-0 p-3">Status</th>
                    <th className="border-0 p-3">Date</th>
                    <th className="border-0 p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id}>
                      <td className="p-3">
                        <div>
                          <div className="fw-semibold text-dark">{submission.name}</div>
                          <div className="small text-muted">{submission.email}</div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="small">
                          <div><strong>{submission.exam}</strong></div>
                          <div>Rank: {submission.rank}</div>
                          <div>Category: {submission.category}</div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="small">
                          <div><strong>{submission.stream}</strong></div>
                          <div>{submission.states.length > 0 ? submission.states.join(', ') : 'Any State'}</div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="small">
                          <div className="fw-semibold">₹{submission.amount_paid}</div>
                          {submission.promo_code && (
                            <div className="text-success">
                              {submission.promo_code} (-₹{submission.discount_amount})
                            </div>
                          )}
                          {submission.payment_id && (
                            <div className="text-muted">{submission.payment_id}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {getStatusBadge(submission.payment_status)}
                      </td>
                      <td className="p-3">
                        <div className="small text-muted">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => viewDetails(submission)}
                          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {submissions.length === 0 && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <Users className="text-muted mx-auto mb-3" size={64} />
                <h3 className="h4 text-dark mb-3">No submissions found</h3>
                <p className="text-muted mb-4">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'No submissions match your current filters.'
                    : 'No personalized list submissions yet.'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetails && selectedSubmission && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Submission Details</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowDetails(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-dark mb-3">Student Information</h6>
                      <div className="vstack gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <Users size={16} className="text-muted" />
                          <span>{selectedSubmission.name}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Mail size={16} className="text-muted" />
                          <span>{selectedSubmission.email}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h6 className="fw-bold text-dark mb-3">Exam Details</h6>
                      <div className="vstack gap-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Exam:</span>
                          <span className="fw-medium">{selectedSubmission.exam}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Rank:</span>
                          <span className="fw-medium">{selectedSubmission.rank}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Category:</span>
                          <span className="fw-medium">{selectedSubmission.category}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Stream:</span>
                          <span className="fw-medium">{selectedSubmission.stream}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h6 className="fw-bold text-dark mb-3">Preferences</h6>
                      <div className="vstack gap-2">
                        <div>
                          <span className="text-muted">States:</span>
                          <div className="mt-1">
                            {selectedSubmission.states.length > 0 ? (
                              selectedSubmission.states.map(state => (
                                <span key={state} className="badge bg-light text-dark me-1 mb-1">
                                  {state}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted">Any State</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h6 className="fw-bold text-dark mb-3">Payment Information</h6>
                      <div className="vstack gap-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Amount:</span>
                          <span className="fw-medium">₹{selectedSubmission.amount_paid}</span>
                        </div>
                        {selectedSubmission.promo_code && (
                          <>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Promo Code:</span>
                              <span className="fw-medium text-success">{selectedSubmission.promo_code}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Discount:</span>
                              <span className="fw-medium text-success">₹{selectedSubmission.discount_amount}</span>
                            </div>
                          </>
                        )}
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Status:</span>
                          <span>{getStatusBadge(selectedSubmission.payment_status)}</span>
                        </div>
                        {selectedSubmission.payment_id && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Payment ID:</span>
                            <span className="fw-medium small">{selectedSubmission.payment_id}</span>
                          </div>
                        )}
                        {selectedSubmission.razorpay_order_id && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Order ID:</span>
                            <span className="fw-medium small">{selectedSubmission.razorpay_order_id}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <h6 className="fw-bold text-dark mb-3">Timeline</h6>
                      <div className="small text-muted">
                        <div>Created: {new Date(selectedSubmission.created_at).toLocaleString()}</div>
                        <div>Updated: {new Date(selectedSubmission.updated_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDetails(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalizedListAdmin;